import { Link } from 'react-router-dom';
import { Button, Column, Grid, Tile } from '@carbon/react';
import {
  IbmSecurity,
  Locked,
  UserAccess,
  DocumentProtected,
  Password,
  Email,
} from '@carbon/icons-react';
import styles from './Security.module.scss';

export default function SecurityPage() {
  return (
    <div className={styles.pageContainer}>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <header className={styles.hero}>
            <p className={`cds--type-label-01 ${styles.eyebrow}`}>
              Confiance & conformité
            </p>
            <h1 className={`cds--type-expressive-heading-04 ${styles.title}`}>
              <IbmSecurity size={32} aria-hidden />
              Sécurité & protection des données
            </h1>
            <p className={`cds--type-body-long-01 ${styles.intro}`}>
              Sphere est une plateforme éducative : elle traite des données
              scolaires et personnelles. Cette page explique, en langage clair,
              comment nous protégeons ces informations — et où gérer vos
              consentements.
            </p>
          </header>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <Grid narrow className={styles.tileGrid}>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.tile}>
                <div className={styles.tileHeader}>
                  <Password size={24} className={styles.tileIcon} aria-hidden />
                  <h2
                    className={`cds--type-productive-heading-03 ${styles.tileTitle}`}
                  >
                    Authentification
                  </h2>
                </div>
                <p className={styles.tileText}>
                  Connexion sécurisée via un fournisseur d’identité dédié
                  (SSO).
                </p>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.tile}>
                <div className={styles.tileHeader}>
                  <UserAccess
                    size={24}
                    className={styles.tileIcon}
                    aria-hidden
                  />
                  <h2
                    className={`cds--type-productive-heading-03 ${styles.tileTitle}`}
                  >
                    Accès par rôle
                  </h2>
                </div>
                <p className={styles.tileText}>
                  Élève, enseignant, direction… chacun ne voit que son
                  périmètre.
                </p>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.tile}>
                <div className={styles.tileHeader}>
                  <Locked size={24} className={styles.tileIcon} aria-hidden />
                  <h2
                    className={`cds--type-productive-heading-03 ${styles.tileTitle}`}
                  >
                    Données protégées
                  </h2>
                </div>
                <p className={styles.tileText}>
                  Fichiers contrôlés (antivirus) et accès authentifié aux
                  ressources.
                </p>
              </Tile>
            </Column>
            <Column lg={4} md={4} sm={4}>
              <Tile className={styles.tile}>
                <div className={styles.tileHeader}>
                  <DocumentProtected
                    size={24}
                    className={styles.tileIcon}
                    aria-hidden
                  />
                  <h2
                    className={`cds--type-productive-heading-03 ${styles.tileTitle}`}
                  >
                    RGPD
                  </h2>
                </div>
                <p className={styles.tileText}>
                  Droits d’accès, rectification et gestion des consentements.
                </p>
              </Tile>
            </Column>
          </Grid>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Authentification
            </h2>
            <div className={styles.body}>
              <p>
                Votre session Sphere s’ouvre via un service d’identité dédié
                (connexion unique / SSO). L’application ne gère pas un
                « mot de passe maison » en parallèle : c’est ce service qui
                authentifie votre compte établissement.
              </p>
              <ul>
                <li>Accès réservé aux utilisateurs autorisés de l’établissement.</li>
                <li>
                  Déconnexion disponible depuis le menu utilisateur (barre du
                  haut).
                </li>
                <li>
                  Profil consultable sur{' '}
                  <Link to="/profile">Mon profil</Link>.
                </li>
              </ul>
            </div>
          </section>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Accès selon votre rôle
            </h2>
            <div className={styles.body}>
              <p>
                Sphere adapte menus et données au rôle métier (enseignant,
                élève, direction, gestionnaire, etc.). Un élève ne voit pas les
                outils d’administration ; un gestionnaire ne crée pas le contenu
                pédagogique d’un enseignant.
              </p>
              <p>
                Le centre d’aide suit la même logique : chaque profil voit
                uniquement ses guides — voir{' '}
                <Link to="/docs">Aide &amp; Guide</Link>.
              </p>
            </div>
          </section>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Données et fichiers
            </h2>
            <div className={styles.body}>
              <p>
                Les informations scolaires (classes, cours, activités, résultats)
                sont accessibles uniquement aux personnes authentifiées et
                autorisées pour votre établissement.
              </p>
              <ul>
                <li>
                  Les documents pédagogiques (PDF, médias…) passent par un
                  contrôle antivirus avant stockage.
                </li>
                <li>
                  Leur consultation exige une session valide — pas de partage
                  « ouvert au public » par défaut.
                </li>
                <li>
                  Les services de la plateforme isolent les domaines métiers
                  (utilisateurs, école, apprentissage…) pour limiter la surface
                  d’exposition.
                </li>
              </ul>
            </div>
          </section>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Vos droits RGPD
            </h2>
            <div className={styles.body}>
              <p>
                Conformément au RGPD, vous pouvez notamment demander l’accès à
                vos données, leur rectification, ou vous opposer à certains
                traitements non essentiels.
              </p>
              <p>
                Les consentements (conditions, confidentialité, cookies,
                analytics, marketing…) se gèrent dans vos paramètres — pas sur
                cette page d’information.
              </p>
            </div>
            <div className={styles.ctaRow}>
              <Button kind="primary" as={Link} to="/settings/privacy">
                Gérer mes consentements
              </Button>
              <Button kind="tertiary" as={Link} to="/settings">
                Paramètres
              </Button>
            </div>
          </section>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Bonnes pratiques
            </h2>
            <div className={styles.body}>
              <ol>
                <li>Ne partagez jamais votre compte ni votre mot de passe.</li>
                <li>
                  Sur un poste partagé (salle info, CDI), déconnectez-vous en
                  quittant Sphere.
                </li>
                <li>
                  Signalez à la direction ou au support tout accès suspect ou
                  erreur de rôle (mauvais menu, mauvais établissement).
                </li>
              </ol>
            </div>
          </section>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <section className={styles.section}>
            <h2
              className={`cds--type-productive-heading-04 ${styles.sectionTitle}`}
            >
              Une question sur la sécurité ?
            </h2>
            <div className={styles.body}>
              <p>
                Pour un problème d’accès ou de compte, commencez par le{' '}
                <Link to="/docs">centre d’aide</Link> adapté à votre rôle, ou
                contactez le support de votre établissement.
              </p>
              <p>
                Pour une demande liée aux données personnelles (droits RGPD),
                adressez-vous au référent / DPO de votre établissement, en plus
                des réglages de{' '}
                <Link to="/settings/privacy">confidentialité</Link>.
              </p>
              <p className={styles.note}>
                <Email size={16} aria-hidden />
                <span>
                  Les coordonnées du DPO peuvent varier selon l’établissement :
                  renseignez-vous auprès de votre direction.
                </span>
              </p>
            </div>
          </section>
        </Column>
      </Grid>
    </div>
  );
}
